/**
 * A binary min-heap over integer items with numeric priorities.
 *
 * Written for depression-filling in the map generator, and used again by A*
 * pathfinding in Phase 3 — the two places in a 4X where the wrong data structure
 * turns a linear algorithm quadratic.
 *
 * Items are plain integers (tile indices, node ids) held in a flat array rather
 * than objects, so a heap of ten thousand tiles costs one allocation, not ten
 * thousand. Ties break by insertion order, which keeps generation deterministic:
 * a heap that broke ties arbitrarily would make identical seeds diverge.
 */
export class MinHeap {
  #items: Int32Array;
  #priorities: Float64Array;
  /** Monotonic counter used only to break priority ties deterministically. */
  #sequence: Int32Array;
  #size = 0;
  #inserted = 0;

  constructor(initialCapacity = 256) {
    const capacity = Math.max(1, initialCapacity);
    this.#items = new Int32Array(capacity);
    this.#priorities = new Float64Array(capacity);
    this.#sequence = new Int32Array(capacity);
  }

  get size(): number {
    return this.#size;
  }

  get isEmpty(): boolean {
    return this.#size === 0;
  }

  push(item: number, priority: number): void {
    if (!Number.isFinite(priority)) {
      throw new Error(`MinHeap priority must be finite, got ${priority}`);
    }
    if (this.#size === this.#items.length) this.#grow();

    let i = this.#size++;
    this.#items[i] = item;
    this.#priorities[i] = priority;
    this.#sequence[i] = this.#inserted++;

    // Sift up.
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (!this.#lessThan(i, parent)) break;
      this.#swap(i, parent);
      i = parent;
    }
  }

  /** Remove and return the lowest-priority item, or undefined when empty. */
  pop(): number | undefined {
    if (this.#size === 0) return undefined;
    const top = this.#items[0]!;

    this.#size--;
    if (this.#size > 0) {
      this.#items[0] = this.#items[this.#size]!;
      this.#priorities[0] = this.#priorities[this.#size]!;
      this.#sequence[0] = this.#sequence[this.#size]!;

      // Sift down.
      let i = 0;
      for (;;) {
        const left = 2 * i + 1;
        const right = left + 1;
        let smallest = i;
        if (left < this.#size && this.#lessThan(left, smallest)) smallest = left;
        if (right < this.#size && this.#lessThan(right, smallest)) smallest = right;
        if (smallest === i) break;
        this.#swap(i, smallest);
        i = smallest;
      }
    }
    return top;
  }

  /** Look at the lowest-priority item without removing it. */
  peek(): number | undefined {
    return this.#size === 0 ? undefined : this.#items[0]!;
  }

  clear(): void {
    this.#size = 0;
    this.#inserted = 0;
  }

  #lessThan(a: number, b: number): boolean {
    const pa = this.#priorities[a]!;
    const pb = this.#priorities[b]!;
    if (pa !== pb) return pa < pb;
    return this.#sequence[a]! < this.#sequence[b]!;
  }

  #swap(a: number, b: number): void {
    const item = this.#items[a]!;
    const priority = this.#priorities[a]!;
    const sequence = this.#sequence[a]!;
    this.#items[a] = this.#items[b]!;
    this.#priorities[a] = this.#priorities[b]!;
    this.#sequence[a] = this.#sequence[b]!;
    this.#items[b] = item;
    this.#priorities[b] = priority;
    this.#sequence[b] = sequence;
  }

  #grow(): void {
    const capacity = this.#items.length * 2;
    const items = new Int32Array(capacity);
    const priorities = new Float64Array(capacity);
    const sequence = new Int32Array(capacity);
    items.set(this.#items);
    priorities.set(this.#priorities);
    sequence.set(this.#sequence);
    this.#items = items;
    this.#priorities = priorities;
    this.#sequence = sequence;
  }
}
